import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router'
import { api, query } from '../api/client'
import type { SupportConversationRow, SupportHistory, SupportMessage } from '../api/support'
import { ReplyForm } from '../components/admin/support/ReplyForm'
import { formatDateTime } from '../lib/format'

/** One account's support conversation: the thread (polled), earlier pages, replies, and close or reopen. */
export function SupportThreadScreen() {
	const { id = '' } = useParams()
	const client = useQueryClient()
	const base = `/api/v1/admin/support/${id}`
	const [older, setOlder] = useState<SupportMessage[]>([])
	const [olderCursor, setOlderCursor] = useState<number | null>(null)
	const [hasOlder, setHasOlder] = useState(false)
	const generation = useRef<number | null>(null)
	const acknowledged = useRef(new Set<string>())
	const conversation = useQuery({
		queryKey: ['admin-support-thread', id],
		queryFn: () => api.get<SupportConversationRow>(base),
	})
	const latest = useQuery({
		queryKey: ['admin-support-messages', id],
		queryFn: () => api.get<SupportHistory>(`${base}/messages`),
		refetchInterval: 10_000,
	})
	const setClosed = useMutation({
		mutationFn: (close: boolean) => api.post(`${base}/${close ? 'close' : 'reopen'}`),
		onSuccess: () => {
			client.invalidateQueries({ queryKey: ['admin-support-thread', id] })
			client.invalidateQueries({ queryKey: ['admin-support'] })
		},
	})

	// A merge changes the generation: whatever earlier pages were loaded no longer line up.
	useEffect(() => {
		const data = latest.data
		if (!data?.conversation) return
		if (generation.current !== data.conversation.generation) {
			generation.current = data.conversation.generation
			setOlder([])
			setOlderCursor(data.olderCursor)
			setHasOlder(data.hasMore)
		}
		const unread = data.messages
			.filter((m) => m.role === 'customer' && !acknowledged.current.has(m.id))
			.map((m) => m.id)
		if (unread.length)
			api.post(`${base}/read`, { ids: unread }).then(() => {
				unread.forEach((m) => acknowledged.current.add(m))
				client.invalidateQueries({ queryKey: ['admin-support'] })
			})
	}, [latest.data, base, client])

	async function loadEarlier() {
		if (olderCursor === null) return
		const page = await api.get<SupportHistory>(`${base}/messages${query({ before: olderCursor })}`)
		setOlder((current) => [...page.messages, ...current])
		setOlderCursor(page.olderCursor)
		setHasOlder(page.hasMore)
	}

	const seen = new Set<string>()
	const messages = [...older, ...(latest.data?.messages ?? [])].filter((m) => !seen.has(m.id) && seen.add(m.id))
	const closed = conversation.data?.closedAt != null

	return (
		<div className="stack">
			<div className="page-header">
				<div>
					<Link to="/admin?tab=support" className="small">
						← Support
					</Link>
					<h1 style={{ margin: '0.2rem 0 0', fontFamily: 'var(--mono)' }}>
						{conversation.data?.supportId ?? 'Conversation'}
					</h1>
					{conversation.data && (
						<div className="muted small">
							<span role="status">{closed ? 'Closed' : 'Open'}</span> ·{' '}
							<Link to={`/admin/accounts/${conversation.data.accountId}`}>Account</Link>
						</div>
					)}
				</div>
				{conversation.data && (
					<button type="button" disabled={setClosed.isPending} onClick={() => setClosed.mutate(!closed)}>
						{closed ? 'Reopen thread' : 'Close thread'}
					</button>
				)}
			</div>
			{(conversation.isError || latest.isError) && (
				<p className="error">{(conversation.error ?? latest.error)?.message}</p>
			)}
			{hasOlder && (
				<button type="button" onClick={loadEarlier}>
					Load earlier messages
				</button>
			)}
			{messages.map((m) => (
				<article key={m.id} className="card stack" style={m.role === 'staff' ? { marginLeft: '3rem' } : undefined}>
					<strong className="small">
						{m.role === 'staff' ? 'Team' : 'Customer'} · {formatDateTime(new Date(m.createdAt))}
						{m.category && (
							<span className="badge" style={{ marginLeft: '0.4rem' }}>
								{m.category}
							</span>
						)}
					</strong>
					<p style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{m.text}</p>
					{m.attachments.map((attachment) => (
						<a key={attachment} href={`${base}/attachments/${attachment}`} target="_blank" rel="noreferrer">
							<img
								src={`${base}/attachments/${attachment}`}
								alt="Attached screenshot"
								style={{ maxWidth: '100%', maxHeight: '60vh' }}
							/>
						</a>
					))}
				</article>
			))}
			{latest.data && !messages.length && <p className="muted">No messages yet.</p>}
			<ReplyForm
				conversationId={id}
				onSent={() => client.invalidateQueries({ queryKey: ['admin-support-messages', id] })}
			/>
		</div>
	)
}
